// IDEVRA GLOBAL HOLDINGS — content & data.

export const IDEVRA = {
  brand: {
    name: "IDEVRA GLOBAL HOLDINGS",
    short: "IDEVRA",
    tagline: "Where Ideas Become Intellectual Property",
    founder: "Syed Amer",
    mark: "/idevra-mark.png",
    wordmark: "/idevra-wordmark.png",
  },

  bootLines: [
    "IDEVRA BIOS v1.07 — INTELLECTUAL PROPERTY ENGINE",
    "MEMORY CHECK ............................. OK",
    "LOADING INNOVATION KERNEL ................ OK",
    "MOUNTING VENTURES [07] ................... OK",
    "PATENT VAULT [14] ........................ SECURED",
    "TRADEMARK REGISTRY [07] .................. SECURED",
    "AUDIO SUBSYSTEM .......................... ONLINE",
    "LEGACY INPUT BUS [KONAMI-86] ............. LISTENING",
    "SYSTEM READY — WELCOME, OPERATOR",
  ],

  intro:
    "A founder-led global intellectual property and innovation holding company — creating, developing, protecting and strategically commercializing next-generation ideas, technologies, software, AI systems, products and intellectual property.",

  principle:
    "Ideas should not remain ideas — they should become intellectual property, technologies, products and companies that create measurable impact.",

  philosophy: {
    line1: "Most companies are built around a product.",
    line2: "IDEVRA is built around the ability to repeatedly create products.",
    body:
      "The company provides a permanent home for the ideas, technologies and intellectual property generated by its founder and its innovation ecosystem — allowing successful concepts to become independent ventures while the underlying intellectual assets remain strategically protected.",
  },

  model: {
    lede:
      "A deliberate separation between intellectual property ownership and commercial operations.",
    create: {
      title: "CREATE & OWN",
      body:
        "IDEVRA GLOBAL HOLDINGS creates and owns intellectual property — proprietary technologies, software platforms, AI systems, inventions, trademarks, patents, designs and methodologies.",
    },
    commercialize: {
      title: "COMMERCIALIZE",
      body:
        "Independently structured operating and technology companies license IDEVRA's innovations through licensing and royalty arrangements — preserving the long-term ownership and strategic value of the underlying IP.",
    },
    outcome:
      "A scalable model in which IDEVRA continuously generates new technologies while individual ventures raise capital, form partnerships, enter new markets and operate independently.",
  },

  coreAreas: [
    { icon: "BrainCircuit", label: "Artificial Intelligence & Intelligent Systems" },
    { icon: "Code2", label: "Software & SaaS Platforms" },
    { icon: "HeartPulse", label: "Digital Healthcare & Medical Technology" },
    { icon: "Layers", label: "Digital Dentistry & 3D-Printing Technologies" },
    { icon: "Workflow", label: "Automation & Intelligent Workflows" },
    { icon: "Database", label: "Data & Computational Systems" },
    { icon: "Binary", label: "Proprietary Algorithms & Methodologies" },
    { icon: "Cpu", label: "Consumer & Enterprise Technologies" },
    { icon: "Rocket", label: "New Product Concepts & Engineering Systems" },
    { icon: "ShieldCheck", label: "Patents, Trademarks, Designs & Other IP" },
  ],

  coreAreasNote:
    "The portfolio is intentionally industry-agnostic. IDEVRA is not designed around a single product or market — it is designed around the continuous creation of valuable intellectual property.",

  ventures: [
    {
      slug: "toothrocket",
      name: "Toothrocket",
      sector: "Digital Dental Manufacturing",
      status: "Operations",
      cover: "/covers/toothrocket.jpg",
      url: "https://toothrocket.com",
      youtube: "https://www.youtube.com/@Tooth.rocket",
      instagram: "https://www.instagram.com/toothrocket",
      film: "https://www.youtube.com/watch?v=2XL4RdlL3E0",
      summary:
        "A digital dental manufacturing platform. SLA-printed clinical devices manufactured at clinical-grade tolerance — the physical spine of the dental group.",
      gallery: "/gallery/toothrocket.jpg",
      phase: 3,
      stats: [
        { label: "PRECISION", value: 94 },
        { label: "AUTOMATION", value: 86 },
        { label: "SCALABILITY", value: 78 },
      ],
      milestones: [
        { label: "SLA clinical device line online", done: true },
        { label: "Clinical-grade tolerance certified", done: true },
        { label: "In-country manufacturing anchor", done: true },
        { label: "Regional network expansion", done: false },
      ],
    },
    {
      slug: "toothmatrix",
      name: "Toothmatrix",
      sector: "AI Dental Laboratory Management",
      status: "Operations",
      cover: "/covers/toothmatrix.jpg",
      url: "https://toothrocket.com/toothmatrix",
      summary:
        "An AI-powered digital dental laboratory management platform deployed in a commercial laboratory — intelligently distributing workloads among technicians by skill, experience, capacity and manufacturing priority, with CAD/CAM digital manufacturing integration.",
      gallery: "/gallery/toothmatrix.jpg",
      phase: 3,
      stats: [
        { label: "AI OPTIMISATION", value: 91 },
        { label: "INTEGRATION", value: 90 },
        { label: "VELOCITY", value: 84 },
      ],
      capabilities: [
        "AI Workload Balancing",
        "Intelligent Case Allocation",
        "Predictive Scheduling",
        "Production Monitoring",
        "Laboratory Analytics",
        "CAD/CAM Integration",
      ],
      milestones: [
        { label: "Deployed in commercial dental laboratory", done: true },
        { label: "AI technician workload balancing live", done: true },
        { label: "Predictive scheduling & lab analytics", done: true },
        { label: "IP & trademark under review — patent pending", done: false },
        { label: "Expanded intelligent production management", done: false },
      ],
    },
    {
      slug: "vigilant",
      name: "Vigilant Entities",
      sector: "AI Incubation & Innovation Platform",
      status: "Pilot",
      cover: "/covers/vigilant.jpg",
      url: "https://vigilantentities.com",
      summary:
        "An AI-powered incubation and innovation platform piloting at Qatar Science & Technology Park (QSTP). A multi-agent architecture of 26 specialised AI personas supports entrepreneurs, mentors, investors and researchers — built on a proprietary startup incubation methodology.",
      gallery: "/gallery/vigilant.jpg",
      phase: 2,
      stats: [
        { label: "INTELLIGENCE", value: 93 },
        { label: "EMPATHY", value: 88 },
        { label: "ADAPTABILITY", value: 90 },
      ],
      capabilities: [
        "26 AI Personas",
        "Multi-Agent Collaboration",
        "Empathy-Aware Interactions",
        "Adaptive Learning",
        "Startup Assessment",
        "Innovation Mentoring",
      ],
      milestones: [
        { label: "Multi-agent architecture of 26 AI personas", done: true },
        { label: "Proprietary incubation methodology developed", done: true },
        { label: "QSTP pilot programme underway", done: true },
        { label: "Ecosystem-wide rollout", done: false },
      ],
    },
    {
      slug: "aetria",
      name: "Aetria OS",
      sector: "AI Workforce Intelligence",
      status: "Pilot",
      cover: "/covers/aetria.jpg",
      url: "https://aetriaos.com/login",
      summary:
        "An enterprise AI Workforce Intelligence platform in pilot testing — predictive skills-gap identification, organisational competency forecasting, intelligent learning pathways and enterprise decision support.",
      gallery: "/gallery/aetria.jpg",
      phase: 2,
      stats: [
        { label: "PREDICTION", value: 90 },
        { label: "INTELLIGENCE", value: 88 },
        { label: "READINESS", value: 60 },
      ],
      capabilities: [
        "Capability Analysis",
        "Skills-Gap Prediction",
        "Competency Forecasting",
        "Learning Pathways",
        "Compliance Monitoring",
        "HR Analytics",
      ],
      milestones: [
        { label: "Workforce intelligence engine built", done: true },
        { label: "Predictive skills-gap models trained", done: true },
        { label: "Enterprise pilot testing underway", done: true },
        { label: "Full enterprise launch", done: false },
      ],
    },
    {
      slug: "qbone",
      name: "Q-Bone Precision",
      sector: "Precision Bio-Manufacturing",
      status: "Concept",
      cover: "/covers/qbone.jpg",
      url: null,
      summary:
        "Precision bone and surgical guides produced at clinical-grade tolerances — built for the operating room, not the catalogue.",
      gallery: "/gallery/qbone.jpg",
      phase: 1,
      stats: [
        { label: "PRECISION", value: 92 },
        { label: "INNOVATION", value: 88 },
        { label: "READINESS", value: 45 },
      ],
      milestones: [
        { label: "Surgical guide concept validated", done: true },
        { label: "Tolerance prototyping underway", done: true },
        { label: "Clinical validation program", done: false },
        { label: "Operating-room deployment", done: false },
      ],
    },
    {
      slug: "dow",
      name: "CareOnWheels",
      sector: "Mobile Clinical Networks",
      status: "Concept",
      cover: "/covers/dow.jpg",
      url: null,
      summary:
        "Mobile clinical units — a full clinic and dental chair inside a van — bringing high-margin digital care workflows to underserved geographies. Logistics as a clinical product.",
      gallery: "/gallery/dow.jpg",
      phase: 0,
      stats: [
        { label: "MOBILITY", value: 90 },
        { label: "REACH", value: 85 },
        { label: "READINESS", value: 30 },
      ],
      milestones: [
        { label: "Mobile clinical unit blueprint", done: true },
        { label: "Route & logistics modelling", done: false },
        { label: "Pilot unit build", done: false },
        { label: "First field deployment", done: false },
      ],
    },
    {
      slug: "aeroforge",
      name: "AeroForge 3D",
      sector: "Aerospace Additive Manufacturing",
      status: "Concept",
      cover: "/covers/aeroforge.jpg",
      url: null,
      summary:
        "Aerospace-grade additive manufacturing — light alloys, complex geometries, certified throughput.",
      gallery: "/gallery/aeroforge.jpg",
      phase: 1,
      stats: [
        { label: "MATERIALS", value: 89 },
        { label: "GEOMETRY", value: 93 },
        { label: "READINESS", value: 40 },
      ],
      milestones: [
        { label: "Light-alloy printing trials", done: true },
        { label: "Complex geometry validation", done: true },
        { label: "Certification pathway", done: false },
        { label: "Certified production throughput", done: false },
      ],
    },
  ],

  vision: {
    heading: "A long-term global platform for invention.",
    body:
      "IDEVRA GLOBAL HOLDINGS is intended to become a long-term global platform for invention, intellectual property ownership, technology development and venture creation.",
    objective:
      "Its objective is not simply to invest in what already exists — but to continuously develop what does not yet exist.",
  },

  patents: {
    office: "Indian Patent Office",
    counts: { granted: 8, prosecution: 2, pending: 1, filed: 1, preparation: 2, pipeline: 4 },
    items: [
      { family: "Agestek", kind: "UTILITY", number: "App 202121013773 · Grant 401064", status: "Granted",
        story: "The flagship of the Agestek family — a utility patent protecting the core mechanism of the Agestek medical device platform, engineered for assistive and clinical use.",
        timeline: [
          { y: "2020", e: "Concept engineering & working prototypes" },
          { y: "2021", e: "Utility application 202121013773 filed — Indian Patent Office" },
          { y: "Granted", e: "Patent No. 401064 issued" },
        ] },
      { family: "Agestek", kind: "DESIGN", number: "353913-001", status: "Granted",
        story: "Registered industrial design protecting the distinctive form factor of the Agestek device — the visual identity of the hardware itself.",
        timeline: [
          { y: "2021", e: "Design application 353913-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter", kind: "UTILITY", number: "App 202221014092", status: "Under Prosecution",
        story: "A precision mechanism that automates the sorting of dental tips — removing a tedious, error-prone manual chore from clinical workflows.",
        timeline: [
          { y: "2021", e: "Mechanism designed & iteratively prototyped" },
          { y: "2022", e: "Utility application 202221014092 filed" },
          { y: "Now", e: "Under examination — prosecution ongoing" },
        ] },
      { family: "Tip Sorter", kind: "DESIGN", number: "358465-001", status: "Granted",
        story: "First of six granted design registrations covering the Tip Sorter's industrial design variants — a defensive ring around the product's appearance.",
        timeline: [
          { y: "2022", e: "Design application 358465-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter", kind: "DESIGN", number: "358466-001", status: "Granted",
        story: "Second granted design registration in the Tip Sorter family — protecting an alternate housing configuration.",
        timeline: [
          { y: "2022", e: "Design application 358466-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter", kind: "DESIGN", number: "358467-001", status: "Granted",
        story: "Third granted design registration — extending protection across the product's component geometry.",
        timeline: [
          { y: "2022", e: "Design application 358467-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter", kind: "DESIGN", number: "358468-001", status: "Granted",
        story: "Fourth granted design registration — covering a further variant in the Tip Sorter design family.",
        timeline: [
          { y: "2022", e: "Design application 358468-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter", kind: "DESIGN", number: "358469-001", status: "Granted",
        story: "Fifth granted design registration — deepening the family's coverage of form-factor variations.",
        timeline: [
          { y: "2022", e: "Design application 358469-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter", kind: "DESIGN", number: "358470-001", status: "Granted",
        story: "Sixth granted design registration — completing the original Tip Sorter design portfolio.",
        timeline: [
          { y: "2022", e: "Design application 358470-001 filed" },
          { y: "Granted", e: "Design registration issued" },
        ] },
      { family: "Tip Sorter — New Design", kind: "UTILITY", number: "App 202321067688", status: "Under Prosecution",
        story: "The next-generation Tip Sorter — a re-engineered mechanism filed as a fresh utility application, building on lessons from the first product cycle.",
        timeline: [
          { y: "2023", e: "New design engineered & validated" },
          { y: "2023", e: "Utility application 202321067688 filed" },
          { y: "Now", e: "Under examination — prosecution ongoing" },
        ] },
      { family: "RefillGenie", kind: "UTILITY", number: "App 202121033373", status: "Filed",
        story: "An automated refill and dispensing concept — filed to secure priority while the product line matures.",
        timeline: [
          { y: "2021", e: "Utility application 202121033373 filed" },
          { y: "Now", e: "Awaiting examination" },
        ] },
      { family: "ToothMatrix", kind: "PLATFORM IP · TRADEMARK", number: "IP & trademark under review", status: "Patent Pending",
        story: "The AI dental laboratory management platform — its proprietary workload-balancing and case-allocation methods are being drafted for protection alongside its trademark.",
        timeline: [
          { y: "2023", e: "Platform live in production" },
          { y: "Now", e: "Patent specification & trademark under review" },
        ] },
      { family: "Vigilant Entities", kind: "PLATFORM IP · METHODOLOGY", number: "Proprietary incubation methodology", status: "In Preparation",
        story: "The 26-persona multi-agent incubation methodology behind Vigilant Entities — piloting at Qatar Science & Technology Park while its specification is prepared.",
        timeline: [
          { y: "2024", e: "Methodology deployed in QSTP pilot" },
          { y: "Now", e: "Specification in preparation" },
        ] },
      { family: "Aetria OS", kind: "PLATFORM IP", number: "Workforce intelligence platform", status: "In Preparation",
        story: "Workforce intelligence platform IP — the algorithms powering Aetria's scheduling and intelligence layers, being documented for filing.",
        timeline: [
          { y: "2024", e: "Platform architecture finalised" },
          { y: "Now", e: "Specification in preparation" },
        ] },
    ],
    pipeline: ["Toothrocket", "Sterimix Pro", "Auto Diluter", "Unicharge"],
    note:
      "Years of technical research, engineering design, iterative prototyping, validation and investment in protecting original innovation — including nanotech-materials additive manufacturing processes engineered for high-precision, high-consistency output.",
  },

  trademarks: {
    office: "Indian Trade Marks Registry",
    counts: { registered: 1, advertised: 3, inReview: 2, applied: 1 },
    items: [
      { mark: "TOOTHROCKET", number: "5813471", cls: "10", filed: "17 Feb 2023", type: "WORDMARK", status: "Registered", validity: "Valid until 17 Feb 2033", goods: "Dental, medical & surgical apparatus and instruments",
        story: "The registered wordmark of the group's flagship venture — Toothrocket, the digital dental manufacturing platform. Fully registered and protected for a decade.",
        timeline: [
          { y: "Feb 2023", e: "Application 5813471 filed — Class 10" },
          { y: "Registered", e: "Wordmark registered — Indian Trade Marks Registry" },
          { y: "2033", e: "Registration valid until 17 Feb 2033" },
        ] },
      { mark: "AGESTEK", number: "4796573", cls: "10", filed: "28 Dec 2020", type: "DEVICE", status: "Applied", goods: "Surgical, medical, dental & veterinary apparatus and instruments",
        story: "Device mark for the Agestek medical hardware family — the brand behind the granted Agestek utility and design patents.",
        timeline: [
          { y: "Oct 2020", e: "First commercial use of the mark" },
          { y: "Dec 2020", e: "Application 4796573 filed — Class 10" },
          { y: "Now", e: "Application under process" },
        ] },
      { mark: "UNICHARGE POWER", number: "4851044", cls: "9", filed: "05 Feb 2021", type: "DEVICE", status: "Advertised", goods: "EV charging stations; electrical & data-processing apparatus",
        story: "The EV-infrastructure brand — charging stations and electrical control systems, staking the group's claim in electric mobility.",
        timeline: [
          { y: "Jan 2021", e: "First commercial use of the mark" },
          { y: "Feb 2021", e: "Application 4851044 filed — Class 9" },
          { y: "Advertised", e: "Published in the Trade Marks Journal" },
        ] },
      { mark: "ORIGIN MOTOR", number: "4851043", cls: "12", filed: "05 Feb 2021", type: "DEVICE", status: "In Review", goods: "Electric vehicles; apparatus for locomotion by land, air or water",
        story: "The electric-vehicle brand of the portfolio — covering vehicles and apparatus for locomotion by land, air or water.",
        timeline: [
          { y: "Jan 2021", e: "First commercial use of the mark" },
          { y: "Feb 2021", e: "Application 4851043 filed — Class 12" },
          { y: "Now", e: "In review with the Registry" },
        ] },
      { mark: "LABSGOGO", number: "4855008", cls: "42", filed: "09 Feb 2021", type: "DEVICE", status: "In Review", goods: "Laboratory analysis & research; scientific & technological services",
        story: "On-demand laboratory services brand — analysis, research and scientific services delivered as a platform.",
        timeline: [
          { y: "Jan 2021", e: "First commercial use of the mark" },
          { y: "Feb 2021", e: "Application 4855008 filed — Class 42" },
          { y: "Now", e: "In review with the Registry" },
        ] },
      { mark: "WATERGAZE", number: "4851042", cls: "42", filed: "05 Feb 2021", type: "DEVICE", status: "Advertised", goods: "Water-quality monitoring & analysis; scientific services",
        story: "Environmental-tech brand for water-quality monitoring and analysis — technology watching over the most vital resource.",
        timeline: [
          { y: "Jan 2021", e: "First commercial use of the mark" },
          { y: "Feb 2021", e: "Application 4851042 filed — Class 42" },
          { y: "Advertised", e: "Published in the Trade Marks Journal" },
        ] },
      { mark: "INDKARTA", number: "4647228", cls: "42", filed: "09 Sep 2020", type: "DEVICE", status: "Advertised", goods: "Scientific & technological services; hardware & software design",
        story: "The earliest mark in the portfolio — a technology-services brand in continuous use since 2018, predating the holding structure itself.",
        timeline: [
          { y: "Jul 2018", e: "First commercial use of the mark" },
          { y: "Sep 2020", e: "Application 4647228 filed — Class 42" },
          { y: "Advertised", e: "Published in the Trade Marks Journal" },
        ] },
    ],
    note: "All marks held in the name of Syed Amer Baqer — spanning medical devices, EV infrastructure, laboratory services and technology platforms.",
  },

  founder: {
    name: "Syed Amer Baqer",
    class: "FOUNDER & CHIEF ARCHITECT",
    level: 25,
    levelLabel: "25+ YEARS ENTERPRISE TECHNOLOGY",
    titles: [
      "AI Advisor to the Royal Family of Qatar",
      "Patent-Holding First Mover",
      "Published Author",
    ],
    bio:
      "Built Toothrocket from nothing — self-funded, bootstrapped R&D in India that grew into a working 3D dental manufacturing laboratory and an AI platform, ToothMatrix, deployed in live commercial production. Now preparing a clinical research programme with Sidra Medicine, Qatar — up to 1,500 patient cases — while scaling AI-driven digital dental manufacturing from India and Qatar to the United States and beyond.",
    bases: ["DOHA · QATAR", "PUNE · INDIA", "GLOBAL · USA-READY"],
    enterprises: ["Oracle", "Microsoft", "OpenText", "Parsons", "Accenture", "EY", "UAE Government"],
    attributes: [
      { label: "INVENTION", value: 95 },
      { label: "AI ARCHITECTURE", value: 92 },
      { label: "MANUFACTURING", value: 90 },
      { label: "LEADERSHIP", value: 93 },
      { label: "RESILIENCE", value: 97 },
    ],
    achievements: [
      { icon: "Rocket", name: "Bootstrap Legend", desc: "Built Toothrocket self-funded — no external funding, no shortcuts" },
      { icon: "Crown", name: "Royal Counsel", desc: "AI Advisor to the Royal Family of Qatar" },
      { icon: "ShieldCheck", name: "Patent Hunter", desc: "Granted multi-family patent portfolio — Indian Patent Office" },
      { icon: "Globe2", name: "Four Continents", desc: "Enterprise technology across India, Middle East, Australia & USA" },
      { icon: "BrainCircuit", name: "Platform Trinity", desc: "Three AI platforms live: ToothMatrix, Vigilant Entities, Aetria OS" },
      { icon: "Sun", name: "Solar Humanitarian", desc: "Solar ICT hubs & malaria-vaccine refrigeration — Tamale, Ghana (UNHCR / Global Shapers)" },
      { icon: "GraduationCap", name: "Clinical Scholar", desc: "PhD research in AI healthcare — Sidra Medicine programme, up to 1,500 patient cases" },
      { icon: "BadgeCheck", name: "ISO Sentinel", desc: "ISO 9001 · 14001 · 17025 · 29993 — Certified Lead Auditor · ITIL" },
    ],
    quests: [
      { years: "2024 — NOW", org: "Toothrocket", role: "Founder — 3D dental manufacturing laboratory, Pune", active: true },
      { years: "2020 — NOW", org: "Indkarta · India", role: "Founder & CEO, R&D — applied research, product engineering, IP development", active: true, link: "https://www.youtube.com/@indkartallc9900", insta: "https://www.instagram.com/indkarta" },
      { years: "2018 — 2020", org: "Indkarta · U.A.E.", role: "Partner & Founder — educational biodomes with Environment Agency Abu Dhabi", link: "https://www.youtube.com/@indkartallc9900", insta: "https://www.instagram.com/indkarta" },
      { years: "2017 — 2018", org: "Ernst & Young", role: "Assistant Manager, ECM Team — U.A.E." },
      { years: "2015 — 2017", org: "Altoview", role: "CEO & Founder — university examinations automation, Abu Dhabi" },
      { years: "2013 — 2015", org: "UAE Government · Health Authority Abu Dhabi", role: "Information Systems Manager" },
      { years: "2013", org: "Accenture", role: "Senior ECM Architect — Australia" },
      { years: "2011 — 2013", org: "Parsons", role: "Airports Automation Consultant — USA" },
      { years: "2009 — 2011", org: "OpenText", role: "Product Specialist — Australia" },
      { years: "2007 — 2009", org: "Microsoft", role: "Deployment Team Leader — Kuwait" },
      { years: "2005 — 2007", org: "Oracle", role: "Functional Consultant — Kuwait" },
    ],
    education: [
      "B.C.S. — Savitribai Phule Pune University (2000–2004)",
      "Creative Writing & Journalism — University of Sydney",
      "Doctoral research (HealthTech) — AI-driven digital healthcare ecosystems (in preparation)",
    ],
  },
};

export const STATUS_META = {
  Operations: { cls: "ops", label: "● OPERATIONS" },
  Pilot: { cls: "pilot", label: "◐ PILOT" },
  Concept: { cls: "concept", label: "○ CONCEPT" },
};

export const PHASES = ["CONCEPT", "PROTOTYPE", "VALIDATION", "OPERATIONS"];

export const CATEGORIES = [
  { id: "home", label: "HOME" },
  { id: "founder", label: "FOUNDER" },
  { id: "vision", label: "VISION" },
  { id: "philosophy", label: "PHILOSOPHY" },
  { id: "model", label: "OUR MODEL" },
  { id: "core", label: "CORE AREAS" },
  { id: "ventures", label: "VENTURES" },
  { id: "vault", label: "IP VAULT" },
];
